import ColorSwatches from '@/components/ColorSwatches';
import { AppState } from '@/store';
import { Button } from '@components/tailwind/index';
import LoadingSpinner from '@components/tailwind/LoadingSpinner';
import { colorAttributeCompare, defaultAttributeCompare } from "@features/pdp/attribute-sorting";
import Swatches from '@features/pdp/components/ProductSwatches/Swatches';
import axios from 'axios';
import classNames from 'classnames';
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAppDispatch } from '@/store';
import CustomModal from './CustomModal';

const swatchesOrder = {
  default: {
    gender: 1,
    name: 2,
    size: 3,
    color: 4,
    // Partner Design Gallery is 5
    // Upsell is 6
    // Personalize with MyRTIC is 7
    material: 8,
    additions: 9,
    width: 10,
    uom: 11,
  },
  cuero: {
    color: 1,
    size: 2,
    name: 3,
    width: 4,
    // Upsell is 6
    gender: 8,
    material: 9,
    additions: 10,
    uom: 11,
  },
};

type Props = {
  variants: any;
  selected: any;
  className?: string;
  onlyShow?: string[];
  swatchSize?: 'sm' | 'md';
  dimensionsScroll?: React.ReactNode;
  onChange: (e) => void;
};
const ProductSwatches = ({
  variants,
  selected,
  className = '',
  onlyShow,
  swatchSize = 'md',
  dimensionsScroll,
  onChange = (e) => {},
}: Props) => {
  const selectedSwatchesOrder = swatchesOrder.default

  const changeItemHandler = (e) => async () => {
    onChange(e);
  };

  const showVariants = useMemo(() => {
    const values = {
      genders: variants?.show?.genders,
      names: variants?.show?.names,
      sizes: variants?.show?.sizes,
      colors: variants?.show?.colors,
      materials: variants?.show?.materials,
      widths: variants?.show?.widths,
      units: variants?.show?.units,
    };

    if (!!onlyShow && onlyShow.length > 0) {
      return {
        genders: onlyShow.includes('genders') && variants?.show?.genders,
        names: onlyShow.includes('names') && variants?.show?.names,
        sizes: onlyShow.includes('sizes') && variants?.show?.sizes,
        colors: onlyShow.includes('colors') && variants?.show?.colors,
        materials: onlyShow.includes('materials') && variants?.show?.materials,
        widths: onlyShow.includes('widths') && variants?.show?.widths,
        units: onlyShow.includes('units') && variants?.show?.units,
      };
    }

    return values;
  }, [onlyShow, variants]);

  const active = useMemo(() => {
    return selected
      ? {
          gender: { ...selected.attributes.all.find((a) => a.key === 'gender'), isAvailable: true },
          name: { ...selected.attributes.all.find((a) => a.key === 'name'), isAvailable: true },
          size: { ...selected.attributes.all.find((a) => a.key === 'size'), isAvailable: true },
          color: { ...selected.attributes.all.find((a) => a.key === 'color'), isAvailable: true },
          material: { ...selected.attributes.all.find((a) => a.key === 'material'), isAvailable: true },
          width: { ...selected.attributes.all.find((a) => a.key === 'width'), isAvailable: true },
          unit: { ...selected.attributes.all.find((a) => a.key === 'unit'), isAvailable: true },
        }
      : null;
  }, [selected]);

  if (!variants) return null;

  return (
    <div className={classNames('tw-flex tw-flex-col tw-gap-3', className)}>
      {showVariants.genders && (
        <Swatches
          label='Gender'
          attributes={variants?.genders?.toSorted(defaultAttributeCompare)}
          activeAttribute={active?.gender}
          itemHandler={changeItemHandler}
          wrapperClassName={`tw-w-full tw-mt-1 tw-mb-3 tw-order-${selectedSwatchesOrder.gender}`}
          indicateSale={false}
          swatchSize={swatchSize}
        />
      )}
      {showVariants.names && (
        <Swatches
          label='Name'
          attributes={variants?.names?.toSorted(defaultAttributeCompare)}
          activeAttribute={active?.name}
          itemHandler={changeItemHandler}
          wrapperClassName={`tw-w-full tw-mt-1 tw-mb-3 tw-order-${selectedSwatchesOrder.name}`}
          indicateSale={false}
          swatchSize={swatchSize}
        />
      )}
      {showVariants.sizes && (
        <>
          {dimensionsScroll}
          <Swatches
            label='Size'
            attributes={variants?.sizes?.toSorted(defaultAttributeCompare)}
            activeAttribute={active?.size}
            itemHandler={changeItemHandler}
            wrapperClassName={`tw-w-full tw-mt-1 tw-mb-3 tw-order-${selectedSwatchesOrder.size}`}
            indicateSale={false}
            swatchSize={swatchSize}
          />
        </>
      )}
      {showVariants.colors && (
        <ColorSwatches
          colors={variants?.colors?.toSorted(colorAttributeCompare)}
          activeColor={active?.color}
          itemHandler={changeItemHandler}
          wrapperClassName={`tw-w-full tw-mt-1 tw-mb-3 tw-order-${selectedSwatchesOrder.color}`}
          indicateSale={false}
          swatchSize={swatchSize}
        />
      )}

      {showVariants.materials && (
        <Swatches
          label='Material'
          attributes={variants?.materials?.toSorted(defaultAttributeCompare)}
          activeAttribute={active?.material}
          itemHandler={changeItemHandler}
          wrapperClassName={`tw-w-full tw-mt-1 tw-mb-3 tw-order-${selectedSwatchesOrder.material}`}
          indicateSale={false}
          swatchSize={swatchSize}
        />
      )}

      {showVariants.widths && (
        <Swatches
          label='Width'
          attributes={variants?.widths?.toSorted(defaultAttributeCompare)}
          activeAttribute={active?.width}
          itemHandler={changeItemHandler}
          wrapperClassName={`tw-w-full tw-mt-1 tw-mb-3 tw-order-${selectedSwatchesOrder.width}`}
          indicateSale={false}
          swatchSize={swatchSize}
        />
      )}

      {showVariants.units && (
        <Swatches
          label='Unit'
          attributes={variants?.units?.toSorted(defaultAttributeCompare)}
          activeAttribute={active?.unit}
          itemHandler={changeItemHandler}
          wrapperClassName={`tw-w-full tw-mt-1 tw-mb-3 tw-order-${selectedSwatchesOrder.uom}`}
          indicateSale={false}
          swatchSize={swatchSize}
        />
      )}
    </div>
  );
};

const FreeGiftModal = (
  {
    className = '',
    title = '',
    id = 'FreeGiftModal',
    promoCodeId,
    itemId = 0,
    lineId = '',
    designId = null,
    action = 'add',
  },
  forwardedRef
) => {
  const dispatch = useAppDispatch();
  const modalRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [choices, setChoices] = useState(null);
  const [selected, setSelected] = useState(null);

  const openModal = useCallback(() => {
    setLoading(true);
    modalRef.current.openModal();
    setTimeout(async () => {
      const { data } = await axios.get(jsRoutes.controllers.Cart.freeGift(promoCodeId).url);
      setChoices(data);
      setSelected(data.items.find((i) => i.id === itemId) || data.items[0]);
      setLoading(false);
    }, 0);
  }, [promoCodeId]);

  const handleSwatch = useCallback(
    (e) => {
      const item = choices.items.find((i) => i.url === e.url);
      setSelected(item);
    },
    [choices]
  );

  const handleAdd = useCallback(() => {
    if (!selected) return;

    if (!!designId && selected.canMiyo) {
      window.location.href =
        selected.url +
        `&design=true&designId=${designId}&lineId=${lineId}${
          !!promoCodeId ? `&promoCodeId=${promoCodeId}` : ''
        }`;
      return;
    }

    setLoading(true);

    setTimeout(async () => {
      const payload = {
        items: [
          lineId && {
            op: 'remove',
            id: itemId,
            quantity: 1,
            promoCodeId,
            lineId,
          },
          {
            op: 'add',
            id: selected.id,
            quantity: 1,
            promoCodeId,
          },
        ].filter((i) => !!i),
      };
      const { data } = await axios.post(jsRoutes.controllers.Cart.update().url, payload);
      window.location.reload();
    }, 0);
  }, [lineId, designId, promoCodeId, selected]);

  useImperativeHandle(
    forwardedRef,
    () => ({
      openModal,
    }),
    [modalRef]
  );

  return (
    <CustomModal ref={modalRef} id={id} title={title} size='sm' className={className}>
      <div>
        {loading && (
          <div className={'tw-w-full tw-text-center'}>
            <LoadingSpinner inline className={'tw-mx-auto'} />
          </div>
        )}
        {!loading && choices?.variants && (
          <div className={'tw-flex tw-w-full tw-flex-row tw-gap-4'}>
            <div className='tw-w-1/4 tw-text-center'>
              <img
                src={selected?.webImages?.default?.thumb?.url}
                alt={selected?.displayName}
                className={'tw-mx-auto tw-inline-block tw-max-h-full tw-max-w-full'}
              />
            </div>
            <div className='tw-w-3/4'>
              <div className='tw-font-bold'>{selected?.displayName}</div>
              <div>
                <span className='tw-line-through'>{selected?.rateFormatted}</span> Free With Purchase
              </div>
              <ProductSwatches variants={choices?.variants} onChange={handleSwatch} selected={selected} />
            </div>
          </div>
        )}
      </div>
      {!!designId && !selected?.canMiyo && (
        <div className='tw-my-3 tw-text-center tw-text-red-500'>
          This selection cannot be personalized. Your design will be removed if you continue.
        </div>
      )}
      <div className='tw-mx-auto tw-mt-8 tw-mb-auto tw-max-w-xs'>
        <Button variant='primary' fullWidth='all' onClick={handleAdd} disabled={!selected}>
          {action === 'add' ? 'Add to cart' : 'Update Cart'}
        </Button>
      </div>
    </CustomModal>
  );
};

const ForwardedFreeGiftModal = forwardRef(FreeGiftModal);
export type FreeGiftModalRef = React.ElementRef<typeof ForwardedFreeGiftModal>;

export default ForwardedFreeGiftModal;
